import useAxiosInterceptor from 'src/services/interceptor';
import { documentLibrary } from '../constants';

type GetAllSubmittedDocuments = {
  therapistUuid: string;
  eocId: string;
  params: object;
  signal: any;
};
class TemplateService {
  private axiosInstance;

  constructor() {
    this.axiosInstance = useAxiosInterceptor();
  }

  getAllTemplates = (payload: any) => {
    const signal = payload?.signal;
    if (payload?.signal) delete payload?.signal;

    return this.axiosInstance.get(documentLibrary.getAllTemplates, {
      params: { ...payload },
      signal,
    });
  };

  callSubmissionAfterSubmit = (payload: object, params: object) => {
    return this.axiosInstance.post(documentLibrary.callSubmissionEvent, payload, {
      params: { ...params },
    });
  };

  getStartOfCareDate = (payload: any) => {
    return this.axiosInstance.get(`${documentLibrary.startOfCare}/${payload?.submissionId}`, {
      params: {
        valueUniqueName: 'startOfCareDate',
        ...payload?.param,
      },
    });
  };

  getSubmittedDocuments = (payload: GetAllSubmittedDocuments) => {
    return this.axiosInstance.get(documentLibrary.getAllSubmittedDocuments(payload.eocId), {
      params: {
        ...payload.params,
      },
      signal: payload?.signal,
    });
  };

  getCompetedDocument = (payload: GetAllSubmittedDocuments) => {
    return this.axiosInstance.get(documentLibrary.getAllSubmittedDocuments(payload.eocId), {
      params: {
        ...payload.params,
      },
      signal: payload?.signal,
    });
  };

  getSubmissionData = (submissionId: string) => {
    return this.axiosInstance.get(`${documentLibrary.getDataBySubmissionId}/${submissionId}`);
  };

  getSignatureTemplate = (payload: object) => {
    return this.axiosInstance.put(documentLibrary.getSignatureTemplate, null, {
      params: { ...payload },
    });
  };

  getAllMiscellaneousDocuments = (payload: any) => {
    return this.axiosInstance.get(
      `${documentLibrary.getAllMiscellaneousDocuments}/${payload?.EocId}`,
      {
        params: payload?.params,
      }
    );
  };

  saveMiscellaneousDocument = (payload: any) => {
    return this.axiosInstance.post(documentLibrary.saveMiscellaneousDocument, payload?.data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        ...payload?.param,
      },
    });
  };

  getAllSupervisiorsByDiscipline = (payload: object) => {
    return this.axiosInstance.get(documentLibrary.getAllSupervisiors, {
      params: {
        ...payload,
      },
    });
  };

  editSignedDocument = (documentUuid: string, payload: object) => {
    return this.axiosInstance.put(`${documentLibrary.editSignDocument}/${documentUuid}`, null, {
      params: { ...payload },
    });
  };

  sendMailToPhysician = (supervisiorUuid: string, payload: object) => {
    return this.axiosInstance.put(
      `${documentLibrary.sendMailToPhysician}/${supervisiorUuid}`,
      null,
      {
        params: { ...payload },
      }
    );
  };

  getCPTorICDcodes = (payload: any, patientUuid: string) => {
    const signal = payload?.signal;
    if (payload?.signal) delete payload?.signal;
    return this.axiosInstance.get(documentLibrary.getCodes(patientUuid), {
      params: { ...payload },
      signal: signal,
    });
  };

  getSignatureAccess = (payload: Array<String>, params: object) => {
    return this.axiosInstance.post(documentLibrary.getSignatureAccess, payload, {
      params: { ...params },
    });
  };

  pullUuid = (payload: object, params = {}) => {
    return this.axiosInstance.post(documentLibrary.pullUuid, payload, {
      params,
    });
  };

  reAssignTherapist = (documentUuid: string, providerUuid: string) => {
    return this.axiosInstance.put(documentLibrary.reAssignTherapist(documentUuid, providerUuid));
  };

  getAllTherapist = (
    discipline: string | null | undefined,
    role: string | null | undefined,
    therapistId: string | null | undefined
  ) => {
    return this.axiosInstance.get(documentLibrary.getAllProviderList(), {
      params: {
        discipline,
        role,
        therapistId,
      },
    });
  };

  getPrefillUrl = (uuid: string, payload: object) => {
    return this.axiosInstance.get(documentLibrary.getPrefillUrl(uuid), {
      params: {
        ...payload,
      },
    });
  };

  deleteRequireddocument = (documentUuid: string) => {
    return this.axiosInstance.delete(documentLibrary.deleteRequiredDocument(documentUuid));
  };

  getPrepareProgressNote = (episodeOfCareId: string, params: object) => {
    return this.axiosInstance.get(documentLibrary.prepareProgressNote(episodeOfCareId), {
      params,
    });
  };

  getSyncFromSignature = (payload: any) => {
    return this.axiosInstance.put(
      documentLibrary.getSyncFromDocumentSignature(payload?.submissionId)
    );
  };

  checkDischargeDocumentAllow = (payload: any) => {
    return this.axiosInstance.get(documentLibrary.checkDischargeDoc(payload?.eocId), {
      params: payload?.params,
    });
  }

  checkPendingDocsAlert = (payload: any) => {
    return this.axiosInstance.get(documentLibrary.checkPendingDocs(payload?.eocId), {
      params: payload?.params,
    });
  }


  newDocSignatureFlow = (payload: any) => {
    return this.axiosInstance.post(`provider/signature/sign-document`, null, {
      params: { ...payload },
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
      },
    });
  };

  undoDocSignatureFlow = (payload: any) => {
    return this.axiosInstance.post(`provider/signature/unsign-document`, null, {
      params: { ...payload },
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
      },
    });
  };
}

const templateService = new TemplateService();

Object.freeze(templateService);

export default templateService;
